import React from 'react';
import type { VisitsHourPoint } from '@troia-v3/types';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { CHART_COLORS } from '@/shared/constants/charts';
import { DashCard, formatNumber } from '@/shared/components/ui/dashboard';

interface Props {
  byHour: VisitsHourPoint[];
  peakHour: number | null;
}

const HourTooltip: React.FC<{ active?: boolean; payload?: Array<{ payload: VisitsHourPoint }> }> = ({
  active,
  payload,
}) => {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;
  return (
    <div
      className="rounded-lg px-3 py-2 text-xs"
      style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', color: '#1e293b' }}
    >
      <div className="font-semibold mb-0.5">{String(point.hour).padStart(2, '0')}h</div>
      {point.measured ? (
        <div>{formatNumber(point.views)} visitas</div>
      ) : (
        // Hora fora do horário de funcionamento cai aqui — e "sem medição" é
        // mais honesto do que desenhar um zero que parece movimento nulo.
        <div style={{ color: '#64748b' }}>Sem medição</div>
      )}
    </div>
  );
};

export const VisitsHourCard: React.FC<Props> = ({ byHour, peakHour }) => (
  <DashCard
    icon="clock"
    title="Visitas por hora do dia"
    subtitle={peakHour !== null ? `Pico às ${String(peakHour).padStart(2, '0')}h` : undefined}
    testId="analytics-visits-hour-card"
  >
    <div style={{ width: '100%', height: 220 }}>
      <ResponsiveContainer>
        <BarChart data={byHour} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--theme-card-border)" />
          <XAxis
            dataKey="hour"
            tickFormatter={(h: number) => String(h).padStart(2, '0')}
            tick={{ fontSize: 10, fill: 'var(--theme-muted-text)' }}
            interval={1}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: 'var(--theme-muted-text)' }}
            axisLine={false}
            tickLine={false}
            width={48}
          />
          <Tooltip content={<HourTooltip />} cursor={{ fill: 'transparent' }} />
          <Bar dataKey="views" radius={[3, 3, 0, 0]}>
            {byHour.map((p) => (
              <Cell
                key={p.hour}
                fill={p.hour === peakHour ? CHART_COLORS[0] : CHART_COLORS[2]}
                data-testid={`analytics-visits-hour-${p.hour}`}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  </DashCard>
);

VisitsHourCard.displayName = 'VisitsHourCard';
